import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getUser, getUUID, saveUser } from "../../../helpers/localStorage";
import { getAllApplications, getProfile, getAllDomains, logOut } from "../../../helpers/api";
import { addParameterToURL } from "../../../helpers/others";
import { getRemaining } from "../../../helpers/dueDate";
import { catalog, heavyDuty } from "./catalog";
import moment from "moment";
import "./applications.css";

/* Color de marca por aplicación, para el banner de cada tarjeta activa.
   Si no está en el mapa, se deriva un tono estable del nombre. */
const ACENTOS = {
  "all data": "#0a5fb0", alldata: "#0a5fb0", "alldata v2": "#0a5fb0",
  identifix: "#f7941d",
  mymitchell: "#1ba0dd", "mitchell connect": "#1ba0dd",
  prodemand: "#d9232c", "mitchell 1 prodemand": "#d9232c",
  techadvisor: "#6d3ff0",
  truckseries: "#edb51e", "mitchell 1 truckseries": "#edb51e",
  shopxpert: "#00b8ab", "taller expert": "#00b8ab",
  kanametek: "#8b3ff5", "kanametek dimensions": "#8b3ff5",
};
function acentoDe(nombre) {
  const k = String(nombre || "").trim().toLowerCase();
  if (ACENTOS[k]) return ACENTOS[k];
  let h = 0;
  for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) % 360;
  return `hsl(${h} 62% 45%)`;
}
function aclarar(hex) {
  if (hex.startsWith("hsl")) return hex.replace(/(\d+)%\)$/, "58%)");
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const f = 1.25;
  r = Math.min(255, Math.round(r * f)); g = Math.min(255, Math.round(g * f)); b = Math.min(255, Math.round(b * f));
  return `rgb(${r} ${g} ${b})`;
}
function iniciales(nombre) {
  return String(nombre || "?").split(/\s+/).slice(0, 2).map((w) => w.charAt(0)).join("").toUpperCase();
}

export default function ApplicationsPage() {
  const navigate = useNavigate();
  const [userApplications, setUserApplications] = useState(getUser().applications);
  const [userDomains, setUserDomains] = useState(getUser().domains || []);
  const [dueDate, setDueDate] = useState(getUser().dueDate || null);
  const [config, setConfig] = useState({ applications: [] });
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [imgFallidas, setImgFallidas] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [filtro, setFiltro] = useState("all");
  const [busqueda, setBusqueda] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    getProfile()
      .then((data) => {
        setUserApplications(data.applications);
        setUserDomains(data.domains || []);
        setDueDate(data.dueDate || null);
        saveUser({ ...getUser(), applications: data.applications, domains: data.domains || [], dueDate: data.dueDate || null });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getAllApplications().then((data) => { setConfig(data); setIsLoading(false); }).catch(() => setIsLoading(false));
    getAllDomains()
      .then((data) => setDomains(data && Array.isArray(data.domains) ? data.domains.filter((d) => d.enabled) : []))
      .catch(() => setDomains([]));
  }, []);

  const openApp = (selectedApp) => {
    if (selectedApp.isDomain) {
      window.open(selectedApp.to, "_blank");
    } else {
      let url = addParameterToURL(selectedApp.to, "igN", getUser().username);
      url = addParameterToURL(url, "igT", getUser().accessToken);
      url = addParameterToURL(url, "igApp", selectedApp.id);
      url = addParameterToURL(url, "uuid", getUUID());
      window.location.href = url;
    }
  };

  // Panel de cuentas de TechAdvisor: solo el super admin. Abre la puerta de
  // TechAdvisor (app 7) con ?destino=panel para gestionar las cuentas de Connect.
  const abrirPanelTech = () => {
    const tech = config.applications.find((a) => a.id === 7);
    const base = (tech && (tech.to || tech.default)) || "https://techadvisor.autotek360.com/entrar";
    let url = addParameterToURL(base, "destino", "panel");
    url = addParameterToURL(url, "igN", getUser().username);
    url = addParameterToURL(url, "igT", getUser().accessToken);
    url = addParameterToURL(url, "igApp", 7);
    url = addParameterToURL(url, "uuid", getUUID());
    window.location.href = url;
  };

  const currentUser = getUser();
  const isAdmin = currentUser.type === 0 || currentUser.type === 1;

  const misApps = config.applications.filter((a) => userApplications.includes(a.id));
  const otrasApps = config.applications.filter((a) => !userApplications.includes(a.id));
  const userDomainStrings = (userDomains || []).map((id) => String(id));
  const misDominios = (Array.isArray(domains) ? domains : [])
    .filter((d) => (isAdmin ? true : userDomainStrings.includes(String(d._id))))
    .map((d) => ({
      img: d.img ? `${process.env.REACT_APP_API.replace("/api", "")}${d.img}` : "",
      tittle: d.name, description: d.description, to: d.url, isDomain: true,
    }));

  const activas = [...misApps, ...misDominios];
  const catalogo = useMemo(() => {
    const disponibles = otrasApps.map((a) => ({ ...a, grupo: "add" }));
    const infoAdd = catalog.map((a) => ({ ...a, grupo: "add", locked: true }));
    const infoHd = heavyDuty.map((a) => ({ ...a, grupo: "hd", locked: true }));
    return [...disponibles, ...infoAdd, ...infoHd];
  }, [otrasApps]);

  const catalogoVisible = catalogo.filter((a) => {
    const okGrupo = filtro === "all" || a.grupo === filtro;
    const okBusqueda = !busqueda || String(a.tittle || "").toLowerCase().includes(busqueda.toLowerCase());
    return okGrupo && okBusqueda;
  });
  const nAdd = catalogo.filter((a) => a.grupo === "add").length;
  const nHd = catalogo.filter((a) => a.grupo === "hd").length;

  const remaining = getRemaining(dueDate);
  const dias = Number.isFinite(remaining.days) ? remaining.days : null;
  // Anillo: para suscripciones normales llena sobre ~90 días; si faltan años,
  // muestra el número en años para que no salga un "8897 DAYS" sin sentido.
  const frac = dias == null ? 1 : Math.max(0, Math.min(1, dias / 90));
  const enAnios = dias != null && Math.abs(dias) > 400;
  const ringNum = dias == null ? "—" : (enAnios ? Math.round(dias / 365) : dias);
  const ringUnit = dias == null ? "" : (enAnios ? (Math.abs(Math.round(dias / 365)) === 1 ? "YEAR" : "YEARS") : "DAYS");
  const circ = 2 * Math.PI * 45;

  const salir = (e) => { e.preventDefault(); logOut(); };
  const irA = (ruta) => () => navigate(ruta);

  const Medallon = ({ app }) => (
    <div className={`pf-tile ${app.img && !imgFallidas[app.img] ? "" : "pf-tile-ini"}`}>
      {app.img && !imgFallidas[app.img] ? (
        <img src={app.img} alt="" onError={() => setImgFallidas((p) => ({ ...p, [app.img]: true }))} />
      ) : (
        <span>{iniciales(app.tittle)}</span>
      )}
    </div>
  );

  return (
    <div className="pf-stage" ref={rootRef}>
      <Modal show={showModal} onHide={() => setShowModal(false)} animation={false} centered>
        <Modal.Header closeButton><Modal.Title>Service Not Available</Modal.Title></Modal.Header>
        <Modal.Body>Contact your administrator to activate this service.</Modal.Body>
      </Modal>

      {/* ---------- barra superior ---------- */}
      <header className="pf-bar">
        <div className="pf-shell">
          <img className="pf-mark" src={`img/${process.env.REACT_APP_BRAND_IMAGE || "bigAutotek.png"}`} alt={process.env.REACT_APP_TITLE || "AUTOTEK360"} />
          <nav className="pf-tabs">
            <button className="pf-tab pf-on" type="button">Applications</button>
            {isAdmin && <button className="pf-tab" type="button" onClick={irA("/user-management")}>User Management</button>}
            {isAdmin && <button className="pf-tab" type="button" onClick={irA("/applications-settings")}>Applications Settings</button>}
            {isAdmin && <button className="pf-tab" type="button" onClick={irA("/domain-management")}>Domain Management</button>}
            {isAdmin && <button className="pf-tab" type="button" onClick={irA("/access-history")}>Access History</button>}
          </nav>
          <div className="pf-right">
            <button className="pf-chip" type="button"><i>{iniciales(currentUser.username)}</i>{currentUser.username}</button>
          </div>
        </div>
      </header>

      <div className="pf-util">
        <div className="pf-shell">
          <span className="pf-where">{process.env.REACT_APP_TITLE || "AUTOTEK360"} &nbsp;/&nbsp; <b>Applications</b></span>
          <div className="pf-links">
            {currentUser.type === 0 && (
              <button type="button" onClick={abrirPanelTech}>Cuentas TechAdvisor</button>
            )}
            <button type="button" onClick={irA("/change-password")}>Change Password</button>
            <button type="button" className="pf-danger" onClick={salir}>Log Out</button>
          </div>
        </div>
      </div>

      {/* ---------- hero ---------- */}
      <div className="pf-hero">
        <div className="pf-art" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/img/heroAutotek.jpg)` }} />
        <div className="pf-shell pf-hero-in">
          <div className="pf-greet">
            <p className="pf-eb">Welcome back</p>
            <h1>{currentUser.username}</h1>
            <p>{activas.length > 0 ? `You have ${activas.length} ${activas.length === 1 ? "service" : "services"} available.` : "Your services will appear here."}</p>
          </div>
          {remaining.level !== "none" && (
            <div className="pf-ring">
              <div className="pf-ringwrap">
                <svg viewBox="0 0 106 106" aria-hidden="true">
                  <circle className="pf-track" cx="53" cy="53" r="45" />
                  <circle className={`pf-arc pf-arc-${remaining.level}`} cx="53" cy="53" r="45"
                    style={{ strokeDasharray: circ, strokeDashoffset: circ * (1 - frac) }} />
                </svg>
                <span><b>{ringNum}</b><small>{ringUnit}</small></span>
              </div>
              <div className="pf-ringtxt">
                <b>{remaining.text}</b>
                <small>{remaining.level === "expired" ? "Expired " : "Renews "}{dueDate ? moment(dueDate).format("MMM D, YYYY") : ""}</small>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------- Launch ---------- */}
      <section className="pf-shell pf-sec">
        <div className="pf-stitle"><h2>Launch</h2><span className="pf-cnt">{activas.length} ACTIVE</span><span className="pf-ln" /></div>
        {isLoading ? (
          <div className="pf-launch">{[0, 1, 2].map((i) => <div key={i} className="pf-big pf-skel" />)}</div>
        ) : activas.length > 0 ? (
          <div className="pf-launch">
            {activas.map((app, i) => {
              const ac = acentoDe(app.tittle), acl = aclarar(ac);
              return (
                <div className="pf-big" key={`a-${i}`} style={{ "--ac": ac, "--acl": acl }} onClick={() => openApp(app)}
                  role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && openApp(app)}
                  aria-label={`Open ${app.tittle}`}>
                  <div className="pf-cardtop"><div className="pf-banner" /><Medallon app={app} /></div>
                  <div className="pf-body">
                    <h3>{app.tittle}</h3>
                    <p>{app.description || ""}</p>
                    <div className="pf-meta">
                      <span className="pf-live"><i />ACTIVE</span>
                      <span className="pf-open">OPEN
                        <svg viewBox="0 0 24 24"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="pf-empty">No services are active on your account yet. Contact your provider to get started.</p>
        )}
      </section>

      {/* ---------- Catalog ---------- */}
      {!isLoading && catalogo.length > 0 && (
        <section className="pf-shell pf-sec">
          <div className="pf-stitle"><h2>Catalog</h2><span className="pf-cnt">{catalogoVisible.length} {catalogoVisible.length === 1 ? "TITLE" : "TITLES"}</span><span className="pf-ln" /></div>
          <div className="pf-toolbar">
            <button className={`pf-fchip ${filtro === "all" ? "pf-on" : ""}`} onClick={() => setFiltro("all")}>All <u>{catalogo.length}</u></button>
            <button className={`pf-fchip ${filtro === "add" ? "pf-on" : ""}`} onClick={() => setFiltro("add")}>Available to add <u>{nAdd}</u></button>
            {nHd > 0 && <button className={`pf-fchip ${filtro === "hd" ? "pf-on" : ""}`} onClick={() => setFiltro("hd")}>Heavy Duty <u>{nHd}</u></button>}
            <div className="pf-find">
              <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" /></svg>
              <input placeholder="Filter by name…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} aria-label="Filter catalog" />
            </div>
          </div>
          <div className="pf-list">
            {catalogoVisible.length === 0 ? (
              <p className="pf-empty">Nothing matches that filter.</p>
            ) : catalogoVisible.map((app, i) => (
              <div className="pf-row" key={`c-${i}`} onClick={() => setShowModal(true)} role="button" tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setShowModal(true)}>
                <Medallon app={app} />
                <div className="pf-nm"><b>{app.tittle}</b></div>
                <div className="pf-ds">{app.description || ""}</div>
                <span className={`pf-tag ${app.grupo === "hd" ? "pf-hd" : ""}`}>{app.grupo === "hd" ? "Heavy duty" : "Available"}</span>
                <div className="pf-act">Request access</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <footer className="pf-foot">
        <div className="pf-shell">
          <span>&copy; {moment().format("YYYY")} <b>{process.env.REACT_APP_TITLE || "AUTOTEK360"}</b> &middot; All rights reserved</span>
          <span>Last sign in &middot; {moment().format("MMM D, YYYY · h:mm A")}</span>
        </div>
      </footer>
    </div>
  );
}
